/**
 * Cash-position visibility layer. Pure projection over manual Account
 * balances + scheduled bills + recent transactions. Imports nothing
 * from budget.ts or models/* — completely isolated. Budget math never
 * reads any of this. Used only by safe-to-spend hero models (PYF,
 * 50/30/20, Zero-Based); the Daily model has its own running balance.
 *
 * Bills due on or before today are materialized as real transactions
 * by `processScheduledBills`, so they're captured in the period
 * expense sum. We only project bills that haven't fired yet:
 *   - upcomingBills: occurrences in (today, periodEnd].
 *   - paidBills: kept in the result shape (always 0) so existing
 *     CashPositionCard callers don't break.
 */

import type {
  Account,
  ScheduledItem,
  Transaction,
} from '../data/types';
import { sumScheduledBillsInRange } from './scheduledOccurrences';

export interface CashPositionResult {
  checkingTotal: number;
  savingsTotal: number;
  creditTotal: number;
  /** Always 0 — kept for backward compatibility with CashPositionCard. */
  paidBills: number;
  /** Bills due strictly after today through period end. */
  upcomingBills: number;
  /**
   * Sum of every expense transaction in [periodStart, today] — manual
   * spending plus bills materialized by processScheduledBills. Field
   * name retained for backward compatibility with the breakdown card.
   */
  spendingSinceUpdate: number;
  pyfRemaining: number;
  safeToSpend: number;
}

const todayIso = (): string => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDaysIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map((s) => Number(s));
  const date = new Date(y!, (m ?? 1) - 1, (d ?? 1) + n);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * Sum of expense transactions whose date falls in [fromIso, toIso]
 * inclusive. Captures both manual entries and bills materialized by
 * processScheduledBills.
 */
function sumExpensesInPeriod(
  transactions: Transaction[],
  fromIso: string,
  toIso: string,
): number {
  let sum = 0;
  for (const t of transactions) {
    if (t.type !== 'expense') continue;
    if (t.date >= fromIso && t.date <= toIso) sum += t.amount;
  }
  return sum;
}

/**
 * Compute the user's cash position for the safe-to-spend hero.
 *
 *   safeToSpend = checkingTotal
 *               − allExpenseTransactionsInPeriod
 *               − upcomingBillsNotYetMaterialized
 *               − pyfRemaining
 *
 * The user enters their current checking balance. Everything spent
 * this period (manual + materialized bills) reduces what's safe;
 * future scheduled bills are projected so the headline accounts for
 * obligations the user hasn't paid yet.
 *
 * Savings and credit balances are reported for awareness only; they
 * never feed into safeToSpend (savings is "untouchable", credit-card
 * payments are already covered by their scheduled bill).
 */
export function computeSafeToSpend(
  accounts: Account[],
  scheduledItems: ScheduledItem[],
  transactions: Transaction[],
  periodStart: string,
  periodEnd: string,
  pyfRemaining: number,
): CashPositionResult {
  let checkingTotal = 0;
  let savingsTotal = 0;
  let creditTotal = 0;

  for (const a of accounts) {
    if (a.type === 'checking') checkingTotal += a.balance;
    else if (a.type === 'savings') savingsTotal += a.balance;
    else if (a.type === 'credit') creditTotal += a.balance;
  }

  const today = todayIso();
  const tomorrow = addDaysIso(today, 1);

  // Manual entries + materialized bills. processScheduledBills only
  // fires for [periodStart, today], so every bill due on/before today
  // is already a Transaction in this sum.
  const allExpenseTransactionsInPeriod = sumExpensesInPeriod(
    transactions,
    periodStart,
    today,
  );

  // Bills due strictly after today haven't been materialized yet —
  // project them so the safe-to-spend number warns about what's still
  // coming this period.
  const upcomingBills =
    tomorrow <= periodEnd
      ? sumScheduledBillsInRange(scheduledItems, tomorrow, periodEnd)
      : 0;

  const safePyf = Math.max(0, pyfRemaining);
  const safeToSpend =
    checkingTotal -
    allExpenseTransactionsInPeriod -
    upcomingBills -
    safePyf;

  return {
    checkingTotal,
    savingsTotal,
    creditTotal,
    paidBills: 0,
    upcomingBills,
    spendingSinceUpdate: allExpenseTransactionsInPeriod,
    pyfRemaining: safePyf,
    safeToSpend,
  };
}
